import React, { useContext, useState, useEffect } from "react";
import { number, object } from "prop-types";

import { AnalyticsContext } from "./../../Analytics/AnalyticsContextWrapper";
import { UserAuthContext } from "./../../User/UserAuthContextWrapper";
import { UnitContext } from "./../UnitContextWrapper";
import { CurrentThemeContext } from "./../../common/ThemeContextWrapper";

import UnitImage, { SmallUnitImage } from "./../styles/UnitImage";
import UnitVariantControl from "./../styles/UnitVariantControl";
import UnitVariantIndicator from "./../styles/UnitVariantIndicator";
import UnitCenterButton from "./../styles/UnitCenterButton";
import UnitShadow from "./../styles/UnitShadow";
import UnitPillar from "./../styles/UnitPillar";
import { CanvasUnit, RemoveUnit } from "./../styles/CanvasUnit";
import MechCustomCategoryDisplay from "./../Category/Mechs/MechCustomCategoryDisplay";
import MechCustomPlusCategoryDisplay from "./../Category/Mechs/MechCustomPlusCategoryDisplay";
import MechBrandedCategoryDisplay from "./../Category/Mechs/MechBrandedCategoryDisplay";
import MechNPCCategoryDisplay from "./../Category/Mechs/MechNPCCategoryDisplay";
import FantasyHeroCategoryDisplay from "./../Category/Heroes/FantasyHeroCategoryDisplay";
import FantasyNPCCategoryDisplay from "./../Category/Heroes/FantasyNPCCategoryDisplay";
import BaseURL from "./../data/images/Hosting-BaseURL";
import { safeName } from "./../Print/PrintAnalytics";

import EntranceImage, { entranceDelay } from "./styles/EntranceImage";
import { SPRITE_KEYS } from "../../common/styles/SpriteImage";

const entranceFantasyImage = `${BaseURL}/buttons/entrance-fantasy.gif`;
const entranceMechImage = `${BaseURL}/buttons/entrance-mech.gif`;

function UnitCanvasDisplay({ index, unit }) {
    const ReactGA = useContext(AnalyticsContext);
    const { user } = useContext(UserAuthContext);
    const { removeUnit, updateVariant } = useContext(UnitContext);
    const { currentTheme } = useContext(CurrentThemeContext);
    const [removeUnitFlag, setRemoveUnitFlag] = useState(false);
    const [mirroredUnitFlag, setMirroredUnitFlag] = useState(false);
    const [entranceFinished, setEntranceFinished] = useState(false);

    const isPremiumUser = user && user.isPremiumUser && !user.isLegendaryUser;
    const isLegendaryUser = user && user.isLegendaryUser;

    let validVariantsArray = unit.variantsArray.filter(unit => !unit.previewSrc);

    if (isLegendaryUser) {
        validVariantsArray = unit.variantsArray.slice();
    } else if (isPremiumUser) {
        validVariantsArray = unit.variantsArray.filter(unit => !unit.legendary);
    }

    const displayVariantControls = validVariantsArray.length > 1;

    let imageSize = 150;

    if (unit.variantsArray[unit.currentVariant].isLarge) {
        imageSize = 200;
    }

    if (unit.variantsArray[unit.currentVariant].isExtraLarge) {
        imageSize = 250;
    }

    if (unit.variantsArray[unit.currentVariant].isMegadeus) {
        imageSize = 300;
    }

    if (unit.variantsArray[unit.currentVariant].isMegadeusPlus) {
        imageSize = 350;
    }

    if (unit.variantsArray[unit.currentVariant].isMegadeusPlusPlus) {
        imageSize = 350; // 400;
    }

    if (unit.variantsArray[unit.currentVariant].isMegadeusOmega) {
        imageSize = 350; // 450;
    }

    if (unit.variantsArray[unit.currentVariant].isExtraSmall) {
        imageSize = unit.variantsArray[unit.currentVariant].width;
    }

    const leftVariantFunction = () => {
        ReactGA.event({
            category: "Canvas Unit",
            action: "Change Variant",
            label: `Previous ${unit.variantsArray[unit.currentVariant].filePrefix} ${safeName(unit.variantsArray[unit.currentVariant].unitNumber)} ${unit.variantsArray[unit.currentVariant].variant}`,
        });

        return updateVariant(unit.id, unit.currentVariant === 0 ? validVariantsArray.length - 1 : unit.currentVariant - 1);
    };

    const rightVariantFunction = () => {
        ReactGA.event({
            category: "Canvas Unit",
            action: "Change Variant",
            label: `Next ${unit.variantsArray[unit.currentVariant].filePrefix} ${safeName(unit.variantsArray[unit.currentVariant].unitNumber)} ${unit.variantsArray[unit.currentVariant].variant}`,
        });

        return updateVariant(unit.id, unit.currentVariant + 1 === validVariantsArray.length ? 0 : unit.currentVariant + 1);
    };

    const removeUnitFunc = (unitID) => {
        if (unit.mechUnit || unit.mechCustomPlusUnit || unit.brandedMechUnit) {
            ReactGA.event({
                category: "Canvas Unit",
                action: "Remove Unit",
                label: unit.mechLabel,
            });
        } else if (unit.fantasyHeroUnit || unit.fantasyNPCUnit) {
            ReactGA.event({
                category: "Canvas Unit",
                action: "Remove Unit",
                label: unit.heroLabel,
            });
        } else {
            ReactGA.event({
                category: "Canvas Unit",
                action: "Remove Unit",
                label: `${unit.variantsArray[unit.currentVariant].filePrefix} ${unit.variantsArray[unit.currentVariant].unitNumber} ${unit.variantsArray[unit.currentVariant].variant}`,
            });
        }

        setRemoveUnitFlag(true);

        return setTimeout(() => removeUnit(unitID), 750);
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setEntranceFinished(true);
        }, entranceDelay);

        return () => clearTimeout(timer);
    }, []);

    if (unit.fantasyHeroUnit) {
        return removeUnitFlag || unit.toBeRemoved ?
            <RemoveUnit
                location={index}
                currentTheme={currentTheme}
                isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
                imageSize={imageSize}
            >
                <FantasyHeroCategoryDisplay
                    unit={unit.variantsArray[unit.currentVariant]}
                    mirroredDisplayFlag={mirroredUnitFlag}
                />
                <UnitCenterButton
                    alt="Remove Unit Button"
                    className={SPRITE_KEYS.xButton}
                    onClick={() => { }}
                    displayControl={false}
                />
            </RemoveUnit> :
            <>
                {entranceFinished ? null :
                    <EntranceImage
                        src={entranceMechImage}
                        location={index}
                        currentTheme={currentTheme}
                        isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                        isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                        isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                        imageSize={imageSize}
                        isFantasy
                    />
                }
                <CanvasUnit
                    location={index}
                    currentTheme={currentTheme}
                    isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                    isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                    isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                    isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                    isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                    isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
                    imageSize={imageSize}
                >
                    <FantasyHeroCategoryDisplay
                        unit={unit.variantsArray[unit.currentVariant]}
                        mirroredDisplayFlag={mirroredUnitFlag}
                        mirroredFunc={() => setMirroredUnitFlag(!mirroredUnitFlag)}
                    />
                    <UnitCenterButton
                        alt="Remove Unit Button"
                        className={SPRITE_KEYS.xButton}
                        onClick={() => removeUnitFunc(unit.id)}
                        displayControl
                    />
                    {(currentTheme === "lvoid" || currentTheme === "dvoid") ?
                        <UnitPillar
                            className={currentTheme === "lvoid" ? "light" : ""}
                            isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                            isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                            isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                            isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                            isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                            isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
                        /> :
                        <UnitShadow
                            className={unit.shadowDetails.size}
                            shadowBottom={unit.shadowDetails.bottom}
                            shadowRight={unit.shadowDetails.right}
                        />
                    }

                </CanvasUnit>
            </>;
    }

    if (unit.fantasyNPCUnit) {
        return removeUnitFlag || unit.toBeRemoved ?
            <RemoveUnit
                location={index}
                currentTheme={currentTheme}
                isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
                imageSize={imageSize}
            >
                <FantasyNPCCategoryDisplay
                    unit={unit.variantsArray[unit.currentVariant]}
                    mirroredDisplayFlag={mirroredUnitFlag}
                />
                <UnitCenterButton
                    alt="Remove Unit Button"
                    className={SPRITE_KEYS.xButton}
                    onClick={() => { }}
                    displayControl={false}
                />
            </RemoveUnit> :
            <>
                {entranceFinished ? null :
                    <EntranceImage
                        src={entranceMechImage}
                        location={index}
                        currentTheme={currentTheme}
                        isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                        isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                        isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                        isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                        isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                        imageSize={imageSize}
                        isFantasy
                    />
                }
                <CanvasUnit
                    location={index}
                    currentTheme={currentTheme}
                    isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                    isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                    isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                    isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                    isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                    isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
                    imageSize={imageSize}
                >
                    <FantasyNPCCategoryDisplay
                        unit={unit.variantsArray[unit.currentVariant]}
                        mirroredDisplayFlag={mirroredUnitFlag}
                        mirroredFunc={() => setMirroredUnitFlag(!mirroredUnitFlag)}
                    />
                    <UnitCenterButton
                        alt="Remove Unit Button"
                        className={SPRITE_KEYS.xButton}
                        onClick={() => removeUnitFunc(unit.id)}
                        displayControl
                    />
                    {(currentTheme === "lvoid" || currentTheme === "dvoid") ?
                        <UnitPillar
                            className={currentTheme === "lvoid" ? "light" : ""}
                            isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                            isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                            isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                            isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                            isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                            isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
                        /> :
                        <UnitShadow
                            className={unit.shadowDetails.size}
                            shadowBottom={unit.shadowDetails.bottom}
                            shadowRight={unit.shadowDetails.right}
                        />
                    }

                </CanvasUnit>
            </>;
    }


    if (unit.mechUnit) {
        return removeUnitFlag || unit.toBeRemoved ?
            <RemoveUnit
                location={index}
                currentTheme={currentTheme}
                isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
                imageSize={imageSize}
            >
                <MechCustomCategoryDisplay
                    unit={unit.variantsArray[unit.currentVariant]}
                    mirroredDisplayFlag={mirroredUnitFlag}
                />
                <UnitCenterButton
                    alt="Remove Unit Button"
                    className={SPRITE_KEYS.xButton}
                    onClick={() => { }}
                    displayControl={false}
                />
            </RemoveUnit> :
            <>
                {entranceFinished ? null :
                    <EntranceImage
                        src={entranceMechImage}
                        location={index}
                        currentTheme={currentTheme}
                        isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                        isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                        isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                        isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                        isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                        imageSize={imageSize}
                        isFantasy={false}
                    />
                }
                <CanvasUnit
                    location={index}
                    currentTheme={currentTheme}
                    isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                    isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                    isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                    isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                    isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                    isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
                    imageSize={imageSize}
                >
                    <MechCustomCategoryDisplay
                        unit={unit.variantsArray[unit.currentVariant]}
                        mirroredDisplayFlag={mirroredUnitFlag}
                        mirroredFunc={() => setMirroredUnitFlag(!mirroredUnitFlag)}
                    />
                    <UnitCenterButton
                        alt="Remove Unit Button"
                        className={SPRITE_KEYS.xButton}
                        onClick={() => removeUnitFunc(unit.id)}
                        displayControl
                    />
                    {(currentTheme === "lvoid" || currentTheme === "dvoid") ?
                        <UnitPillar
                            className={currentTheme === "lvoid" ? "light" : ""}
                            isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                            isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                            isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                            isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                            isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                            isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
                        /> :
                        <UnitShadow
                            className={unit.shadowDetails.size}
                            shadowBottom={unit.shadowDetails.bottom}
                            shadowRight={unit.shadowDetails.right}
                        />
                    }
                </CanvasUnit>
            </>;
    }

    if (unit.mechCustomPlusUnit) {
        return removeUnitFlag || unit.toBeRemoved ?
            <RemoveUnit
                location={index}
                currentTheme={currentTheme}
                isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                isExtraLarge={true}
                isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                isMegadeusPlus={true}
                isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
                imageSize={imageSize}
            >
                <MechCustomPlusCategoryDisplay
                    unit={unit.variantsArray[unit.currentVariant]}
                    mirroredDisplayFlag={mirroredUnitFlag}
                />
                <UnitCenterButton
                    alt="Remove Unit Button"
                    className={SPRITE_KEYS.xButton}
                    onClick={() => { }}
                    displayControl={false}
                />
            </RemoveUnit> :
            <>
                {entranceFinished ? null :
                    <EntranceImage
                        src={entranceMechImage}
                        location={index}
                        currentTheme={currentTheme}
                        isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                        isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                        isExtraLarge={true}
                        isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                        isMegadeusPlus={true}
                        imageSize={imageSize}
                        isFantasy={false}
                    />
                }
                <CanvasUnit
                    location={index}
                    currentTheme={currentTheme}
                    isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                    isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                    isExtraLarge={true}
                    isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                    isMegadeusPlus={true}
                    isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
                    imageSize={imageSize}
                >
                    <MechCustomPlusCategoryDisplay
                        unit={unit.variantsArray[unit.currentVariant]}
                        mirroredDisplayFlag={mirroredUnitFlag}
                        mirroredFunc={() => setMirroredUnitFlag(!mirroredUnitFlag)}
                    />
                    <UnitCenterButton
                        alt="Remove Unit Button"
                        className={SPRITE_KEYS.xButton}
                        onClick={() => removeUnitFunc(unit.id)}
                        displayControl
                    />
                    {(currentTheme === "lvoid" || currentTheme === "dvoid") ?
                        <UnitPillar
                            className={currentTheme === "lvoid" ? "light" : ""}
                            isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                            isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                            isExtraLarge={true}
                            isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                            isMegadeusPlus={true}
                            isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
                        /> :
                        <UnitShadow
                            className={unit.shadowDetails.size}
                            shadowBottom={unit.shadowDetails.bottom}
                            shadowRight={unit.shadowDetails.right}
                        />
                    }
                </CanvasUnit>
            </>;
    }

    if (unit.brandedMechUnit) {
        return removeUnitFlag || unit.toBeRemoved ?
            <RemoveUnit
                location={index}
                currentTheme={currentTheme}
                isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
                imageSize={imageSize}
            >
                <MechBrandedCategoryDisplay
                    unit={unit.variantsArray[unit.currentVariant]}
                    mirroredDisplayFlag={mirroredUnitFlag}
                />
                <UnitCenterButton
                    alt="Remove Unit Button"
                    className={SPRITE_KEYS.xButton}
                    onClick={() => { }}
                    displayControl={false}
                />
            </RemoveUnit> :
            <>
                {entranceFinished ? null :
                    <EntranceImage
                        src={entranceMechImage}
                        location={index}
                        currentTheme={currentTheme}
                        isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                        isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                        isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                        isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                        isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                        imageSize={imageSize}
                        isFantasy={false}
                    />
                }
                <CanvasUnit
                    location={index}
                    currentTheme={currentTheme}
                    isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                    isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                    isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                    isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                    isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                    isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
                    imageSize={imageSize}
                >
                    <MechBrandedCategoryDisplay
                        unit={unit.variantsArray[unit.currentVariant]}
                        mirroredDisplayFlag={mirroredUnitFlag}
                        mirroredFunc={() => setMirroredUnitFlag(!mirroredUnitFlag)}
                    />
                    <UnitCenterButton
                        alt="Remove Unit Button"
                        className={SPRITE_KEYS.xButton}
                        onClick={() => removeUnitFunc(unit.id)}
                        displayControl
                    />
                    {(currentTheme === "lvoid" || currentTheme === "dvoid") ?
                        <UnitPillar
                            className={currentTheme === "lvoid" ? "light" : ""}
                            isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                            isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                            isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                            isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                            isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                            isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
                        /> :
                        <UnitShadow
                            className={unit.shadowDetails.size}
                            shadowBottom={unit.shadowDetails.bottom}
                            shadowRight={unit.shadowDetails.right}
                        />
                    }
                </CanvasUnit>
            </>;
    }

    if (unit.npcMechUnit) {
        return removeUnitFlag || unit.toBeRemoved ?
            <RemoveUnit
                location={index}
                currentTheme={currentTheme}
                isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
                imageSize={imageSize}
            >
                <MechNPCCategoryDisplay
                    unit={unit.variantsArray[unit.currentVariant]}
                    mirroredDisplayFlag={mirroredUnitFlag}
                />
                <UnitCenterButton
                    alt="Remove Unit Button"
                    className={SPRITE_KEYS.xButton}
                    onClick={() => { }}
                    displayControl={false}
                />
            </RemoveUnit> :
            <>
                {entranceFinished ? null :
                    <EntranceImage
                        src={entranceMechImage}
                        location={index}
                        currentTheme={currentTheme}
                        isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                        isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                        isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                        isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                        isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                        imageSize={imageSize}
                        isFantasy={false}
                    />
                }
                <CanvasUnit
                    location={index}
                    currentTheme={currentTheme}
                    isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                    isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                    isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                    isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                    isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                    isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
                    imageSize={imageSize}
                >
                    <MechNPCCategoryDisplay
                        unit={unit.variantsArray[unit.currentVariant]}
                        mirroredDisplayFlag={mirroredUnitFlag}
                        mirroredFunc={() => setMirroredUnitFlag(!mirroredUnitFlag)}
                    />
                    <UnitCenterButton
                        alt="Remove Unit Button"
                        className={SPRITE_KEYS.xButton}
                        onClick={() => removeUnitFunc(unit.id)}
                        displayControl
                    />
                    {(currentTheme === "lvoid" || currentTheme === "dvoid") ?
                        <UnitPillar
                            className={currentTheme === "lvoid" ? "light" : ""}
                            isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                            isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                            isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                            isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                            isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                            isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
                        /> :
                        <UnitShadow
                            className={unit.shadowDetails.size}
                            shadowBottom={unit.shadowDetails.bottom}
                            shadowRight={unit.shadowDetails.right}
                        />
                    }
                </CanvasUnit>
            </>;
    }

    return removeUnitFlag || unit.toBeRemoved ?
        <RemoveUnit
            location={index}
            currentTheme={currentTheme}
            isRegular={unit.variantsArray[unit.currentVariant].isRegular}
            isLarge={unit.variantsArray[unit.currentVariant].isLarge}
            isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
            isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
            isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
            isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
            imageSize={imageSize}
        >
            <UnitVariantIndicator
                currentVariant={unit.currentVariant}
                isPremium={!!unit.variantsArray[unit.currentVariant].previewSrc}
                isLegendary={unit.variantsArray[unit.currentVariant].legendary}
                numVariants={unit.variantsArray.length}
                unitID={`${unit.variantsArray[unit.currentVariant].filePrefix}${unit.variantsArray[unit.currentVariant].unitNumber}-${unit.variantsArray[unit.currentVariant].variant}`}
            />
            <UnitVariantControl
                alt="Previous Variant"
                className={SPRITE_KEYS.lArrow}
                onClick={leftVariantFunction}
                displayControl={false}
            />
            {
                    unit.variantsArray[unit.currentVariant].isExtraSmall ?
                    <SmallUnitImage
                        className={mirroredUnitFlag ? "mirrored" : ""}
                        src={unit.variantsArray[unit.currentVariant].src}
                        alt={`${unit.variantsArray[unit.currentVariant].filePrefix}${unit.variantsArray[unit.currentVariant].unitNumber}-${unit.variantsArray[unit.currentVariant].variant}`}
                        height={unit.variantsArray[unit.currentVariant].height}
                        width={unit.variantsArray[unit.currentVariant].width}
                    /> :
                    <UnitImage
                        className={mirroredUnitFlag ? "mirrored" : ""}
                        src={unit.variantsArray[unit.currentVariant].src}
                        alt={`${unit.variantsArray[unit.currentVariant].filePrefix}${unit.variantsArray[unit.currentVariant].unitNumber}-${unit.variantsArray[unit.currentVariant].variant}`}
                    />
                }
            <UnitVariantControl
                alt="Next Variant"
                className={SPRITE_KEYS.rArrow}
                onClick={rightVariantFunction}
                displayControl={false}
            />

            <UnitCenterButton
                alt="Remove Unit Button"
                className={SPRITE_KEYS.xButton}
                onClick={() => { }}
                displayControl={false}
            />
        </RemoveUnit> :
        <>
            {entranceFinished ? null :
                <EntranceImage
                    src={entranceFantasyImage}
                    location={index}
                    currentTheme={currentTheme}
                    isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                    isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                    isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                    isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                    isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                    imageSize={imageSize}
                    isFantasy={!unit.singleMechUnit}
                />
            }
            <CanvasUnit
                location={index}
                currentTheme={currentTheme}
                isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
                imageSize={imageSize}
            >
                <UnitVariantIndicator
                    currentVariant={unit.currentVariant}
                    isPremium={!!unit.variantsArray[unit.currentVariant].previewSrc}
                    isLegendary={unit.variantsArray[unit.currentVariant].legendary}
                    numVariants={unit.variantsArray.length}
                    unitID={`${unit.variantsArray[unit.currentVariant].filePrefix}${unit.variantsArray[unit.currentVariant].unitNumber}-${unit.variantsArray[unit.currentVariant].variant}`}
                />
                <UnitVariantControl
                    alt="Previous Variant"
                    className={SPRITE_KEYS.lArrow}
                    onClick={leftVariantFunction}
                    displayControl={displayVariantControls}
                />
                {
                    unit.variantsArray[unit.currentVariant].isExtraSmall ?
                    <SmallUnitImage
                        className={mirroredUnitFlag ? "mirrored" : ""}
                        src={unit.variantsArray[unit.currentVariant].src}
                        alt={`${unit.variantsArray[unit.currentVariant].filePrefix}${unit.variantsArray[unit.currentVariant].unitNumber}-${unit.variantsArray[unit.currentVariant].variant}`}
                        onClick={() => setMirroredUnitFlag(!mirroredUnitFlag)}
                        auraColor={unit.variantsArray[unit.currentVariant].aura}
                        height={unit.variantsArray[unit.currentVariant].height}
                        width={unit.variantsArray[unit.currentVariant].width}
                    /> :
                    <UnitImage
                        className={mirroredUnitFlag ? "mirrored" : ""}
                        src={unit.variantsArray[unit.currentVariant].src}
                        alt={`${unit.variantsArray[unit.currentVariant].filePrefix}${unit.variantsArray[unit.currentVariant].unitNumber}-${unit.variantsArray[unit.currentVariant].variant}`}
                        onClick={() => setMirroredUnitFlag(!mirroredUnitFlag)}
                        auraColor={unit.variantsArray[unit.currentVariant].aura}
                    />
                }
                <UnitVariantControl
                    alt="Next Variant"
                    className={SPRITE_KEYS.rArrow}
                    onClick={rightVariantFunction}
                    displayControl={displayVariantControls}
                />

                <UnitCenterButton
                    alt="Remove Unit Button"
                    className={SPRITE_KEYS.xButton}
                    onClick={() => removeUnitFunc(unit.id)}
                    displayControl
                />
                {(currentTheme === "lvoid" || currentTheme === "dvoid") ?
                    <UnitPillar
                        className={currentTheme === "lvoid" ? "light" : ""}
                        isRegular={unit.variantsArray[unit.currentVariant].isRegular}
                        isLarge={unit.variantsArray[unit.currentVariant].isLarge}
                        isExtraLarge={unit.variantsArray[unit.currentVariant].isExtraLarge}
                        isMegadeus={unit.variantsArray[unit.currentVariant].isMegadeus}
                        isMegadeusPlus={unit.variantsArray[unit.currentVariant].isMegadeusPlus}
                        isExtraSmall={unit.variantsArray[unit.currentVariant].isExtraSmall}
                    /> :
                    <UnitShadow
                        className={unit.shadowDetails.size}
                        shadowBottom={unit.shadowDetails.bottom}
                        shadowRight={unit.shadowDetails.right}
                    />
                }
            </CanvasUnit>
        </>;
}

UnitCanvasDisplay.propTypes = {
    index: number.isRequired,
    unit: object.isRequired,
};

export default UnitCanvasDisplay;
